<template>
  <div v-if="hasAnswer(answer) && isFileType(question)">
    <h4
      class="question_file__title"
      :class="{ required: isRequired(question) }"
      v-html="sanitizedQuestion + (isRequired(question) ? ' *' : '')"
    ></h4>
    <p class="question_file__description">{{ question.attributes.description }}</p>

    <label v-if="!file" class="question_file__input-group" :for="inputId">
      <p><span>{{ uploading ? "Enviando..." : "Anexar arquivo" }}</span></p>
      <input
        class="question_file__input"
        :id="inputId"
        type="file"
        :accept="accept"
        :disabled="uploading"
        @change="handleChange"
      />
    </label>

    <div v-else class="question_file__uploaded">
      <div class="question_file__success">
        <div class="divider"></div>
        <p>{{ file.name }}</p>
        <button type="button" @click="removeFile">Excluir</button>
      </div>
      <div v-if="previewUrl" class="question_file__preview">
        <iframe
          v-if="previewKind === 'pdf'"
          :src="previewUrl"
          :title="file.name"
          class="question_file__preview-frame"
        />
        <img v-else :src="previewUrl" :alt="file.name" class="question_file__preview-image" />
      </div>
    </div>

    <p class="question_file__hint" :class="{ 'question_file__hint--error': errorMessage }">
      {{ errorMessage || hint }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.question_file__title {
  font-size: 16px;
  font-weight: 400;
  line-height: 18.75px;
  color: #4F4F4F;
  margin: 10px 0;

  ::v-deep p {
    margin: 8px 0;
    line-height: 1.4;
  }

  ::v-deep a {
    color: #4169E1;
    text-decoration: underline;
  }
}

.question_file__description {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.41px;
  color: #4F4F4F;
  margin-bottom: 10px;
}

.question_file__input-group {
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  border: 0.5px dashed #4169E1;
  cursor: pointer;

  p {
    text-align: center;
    font-size: 14px;
    color: #A9A9A9;

    span {
      font-size: 16px;
      text-decoration: underline;
      color: #4f4f4f;
    }
  }
}

.question_file__input {
  width: 0;
  height: 0;
  visibility: hidden;
}

.question_file__uploaded {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question_file__success {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 24px;
  position: relative;
  gap: 8px;
  border-radius: 8px;
  border: 0.5px solid #A8A8A8;
  background: #FFF;

  .divider {
    width: 8px;
    height: 56px;
    position: absolute;
    left: 0;
    border-radius: 8px 0 0 8px;
    background: rgba(48, 158, 58, 0.5);
  }

  p {
    flex: 1;
    color: #333;
    font-size: 16px;
    overflow-wrap: anywhere;
  }

  button {
    border: none;
    background: transparent;
    height: 40px;
    cursor: pointer;
    color: #4F4F4F;
    text-decoration-line: underline;
  }
}

.question_file__preview {
  width: 100%;
  height: 260px;
  border-radius: 8px;
  overflow: auto;
  border: 0.5px solid #A8A8A8;
  background: #fff;
}

.question_file__preview-frame {
  width: 100%;
  height: 260px;
  border: 0;
  display: block;
}

.question_file__preview-image {
  width: 100%;
  display: block;
}

.question_file__hint {
  font-size: 12px;
  color: #767676;
  margin-top: 6px;

  &--error {
    color: #d82b24;
  }
}

.required {
  color: #333333;
}
</style>

<script>
import { sanitizeBasicHtml } from "@/utils/htmlSanitizer";
import { uploadErrorMessage } from "@/services/uploadError";

const DEFAULT_MAX_SIZE_KB = 10 * 1024;

function extensionOf(name) {
  const match = /\.([^.]+)$/.exec(name || "");
  return match ? match[1].toLowerCase() : "";
}

function listOr(items) {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} ou ${items[items.length - 1]}`;
}

function listAnd(items) {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`;
}

export default {
  name: "question-file",
  props: {
    answer: {
      type: Object,
      required: true
    },
    question: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    file: null,
    previewUrl: null,
    previewKind: null,
    uploading: false,
    errorMessage: ""
  }),
  computed: {
    sanitizedQuestion() {
      return sanitizeBasicHtml(this.question.question);
    },
    inputId() {
      return `question-file-${this.question._id}`;
    },
    extensions() {
      return (this.question.attributes && this.question.attributes.extensions) || [];
    },
    maxSizeKb() {
      return (this.question.attributes && this.question.attributes.max_size_kb) || DEFAULT_MAX_SIZE_KB;
    },
    accept() {
      const mimes = (this.question.attributes && this.question.attributes.mimes) || [];
      return [...this.extensions.map(ext => `.${ext}`), ...mimes].join(",");
    },
    hint() {
      return `Aceita ${listAnd(this.extensions)} até ${Math.round(this.maxSizeKb / 1024)} MB`;
    }
  },
  methods: {
    hasAnswer: answer => Object.keys(answer).length > 0,
    isFileType: question => question.type === "file",
    isRequired: question => question.required,

    async handleChange(event) {
      const input = event.target;
      let file = input.files && input.files[0];
      input.value = "";
      if (!file) return;

      this.errorMessage = "";

      if (!this.extensions.includes(extensionOf(file.name))) {
        this.errorMessage = `Formato não aceito. Envie um arquivo ${listOr(this.extensions)}.`;
        return;
      }

      if (file.size > this.maxSizeKb * 1024) {
        this.errorMessage = `Arquivo maior que ${Math.round(this.maxSizeKb / 1024)} MB. Envie um arquivo menor.`;
        return;
      }

      file = await this.convertHeic(file);

      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append("question_id", this.question._id);
        formData.append("file", file);

        const response = await this.$http.post("/api/v2/questions/files", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        this.answer.value = response.data.data.id;
        this.file = file;
        this.setPreview(file);
      } catch (error) {
        this.errorMessage = uploadErrorMessage(error, file, this.maxSizeKb * 1024);
      } finally {
        this.uploading = false;
      }
    },

    // O navegador não desenha HEIC: vira JPG antes de subir. Falhou, sobe o HEIC e o recrutador só baixa.
    async convertHeic(file) {
      const isHeic = extensionOf(file.name) === "heic" || /^image\/hei[cf]$/.test(file.type);
      if (!isHeic) return file;

      try {
        const { default: heic2any } = await import("heic2any");
        const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        return new File([blob], file.name.replace(/\.heic$/i, ".jpg"), { type: "image/jpeg" });
      } catch (_) {
        return file;
      }
    },

    setPreview(file) {
      this.revokePreview();
      const isPdf = file.type === "application/pdf" || extensionOf(file.name) === "pdf";
      const isImage = /^image\//.test(file.type) || ["jpg", "jpeg", "png"].includes(extensionOf(file.name));
      if (!isPdf && !isImage) return;
      this.previewKind = isPdf ? "pdf" : "image";
      this.previewUrl = URL.createObjectURL(file);
    },

    revokePreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.previewKind = null;
    },

    removeFile() {
      this.revokePreview();
      this.file = null;
      this.answer.value = "";
      this.errorMessage = "";
    }
  },
  beforeDestroy() {
    this.revokePreview();
  }
};
</script>
